import styled from 'styled-components'
import { ActiveInventoryItem } from 'schema/contract'
import Toggle from '../../../components/toggle'
import { convertPrice } from '../../../utils'
import ListFiledBuildingName from './list-filed-building-name'
import { useIsSp } from 'hooks'
import theme from 'styles/theme'

type BackgroundType = 'gray' | 'white'

const CardSize = {
  max: '400px',
  min: '320px',
} as const

const Container = styled.div<{
  isVertical?: boolean
  background?: BackgroundType
  onClick?: () => void
  isSelected?: boolean
  noSelectedMode?: boolean
  width?: string
}>`
  display: flex;
  flex-direction: column;
  /* PC版の幅計算
   * isVertical=true: gridレイアウト内で使用される場合、100%幅
   * isVertical=false: ListWrapper（flexbox）内で3列表示
   *   100% - 40px = カード間のマージン（20px × 2）を引いた幅
   *   その幅を3等分してカード1枚の幅とする
   */
  width: ${(p) => {
    if (p.width) return p.width
    if (p.isVertical) return '100%'
    return 'calc((100% - 40px) / 3)'
  }};
  max-width: ${CardSize.max};
  min-width: ${CardSize.min};
  padding: 24px;
  background-color: ${(p) => {
    switch (p.background) {
      case 'white':
        return p.theme.WHITE
      case 'gray':
        return p.theme.BACKGROUND_BASE
      default:
        return p.theme.BACKGROUND_BASE
    }
  }};
  border: ${(p) =>
    !p.noSelectedMode && p.isSelected
      ? `2px solid ${p.theme.MAIN}`
      : '1px solid #E5E7EB'};
  border-radius: 8px;
  box-sizing: border-box;
  cursor: ${(p) => (p.onClick ? 'pointer' : 'default')};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: ${(p) => {
      if (p.width) return p.width
      if (p.isVertical) return '100%'
      // 横スクロール用に固定幅320px
      return `${CardSize.min}`
    }};
    min-width: ${(p) => {
      if (p.width) return p.width
      if (p.isVertical) return 'auto'
      return `${CardSize.min}`
    }};
    max-width: ${(p) => (p.isVertical ? '100%' : 'none')};
    box-sizing: border-box;
    margin: 0;
    flex-shrink: 0;
  }
`

const PropertyHeader = styled.div`
  margin-bottom: 16px;
`

const PropertyName = styled.a`
  font-size: ${(p) => p.theme.font.size.l};
  font-weight: bold;
  color: #a16500;
  text-decoration: underline;
  display: block;
  margin-bottom: 4px;
`

const PropertyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #374151;
`

const PropertyDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLabel = styled.span`
  color: ${(p) => p.theme.FONT_COLOR};
  font-size: ${(p) => p.theme.font.size.m};
`

const PropertyValue = styled.span<{ color?: string; bold?: boolean }>`
  font-weight: ${(p) => (p.bold ? '600' : '400')};
  color: ${(p) => p.color || p.theme.FONT_COLOR};
  font-size: ${(p) => p.theme.font.size.m};
`

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e5e7eb;
  margin: 8px 0;
`

const ToggleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #374151;
`

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ToggleLabel = styled.span`
  color: ${(p) => p.color || p.theme.FONT_COLOR};
  font-size: ${(p) => p.theme.font.size.m};
  line-height: 1.4;
`

const PinLabel = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${(p) => p.theme.MAIN};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 12px;
  color: white;
  font-weight: bold;
`

export interface PropertyExampleListItemProps {
  item: ActiveInventoryItem & { itemNumberLabel?: string }
  isVisibleToggle?: boolean
  onChangeToggle?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  isVertical?: boolean
  background?: BackgroundType
  onClick?: () => void
  isSelected?: boolean
  noSelectedMode?: boolean
  width?: string
}

const PinNumberLabel = ({ label }: { label?: string }) => {
  if (label === undefined) {
    return <></>
  }

  return <PinLabel>{label}</PinLabel>
}

export const PropertyExampleListItem = ({
  item,
  isVisibleToggle,
  onChangeToggle,
  isVertical,
  background = 'gray',
  onClick,
  isSelected = false,
  noSelectedMode = false,
  width,
}: PropertyExampleListItemProps) => {
  const isSP = useIsSp(theme.vp)
  const formatYM = (ym: string) => {
    // yyyy-mm -> yyyy/mm
    return `${ym.slice(0, 4)}/${ym.slice(5, 7)}`
  }

  const formattedName =
    isSP && item.name.length > 12 ? item.name.slice(0, 11) + '･･･' : item.name

  return (
    <Container
      isVertical={isVertical}
      background={background}
      onClick={onClick}
      isSelected={isSelected}
      noSelectedMode={noSelectedMode}
      width={width}
    >
      <PropertyHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PinNumberLabel label={item.itemNumberLabel} />
          <PropertyName as="div">
            <ListFiledBuildingName name={formattedName} url={item.url} />
          </PropertyName>
        </div>
        <PropertyValue bold>
          {item.unitSize}m² / {item.floorPlan} / {item.story}階
        </PropertyValue>
      </PropertyHeader>

      <PropertyDetails>
        <PropertyDetailRow>
          <PropertyLabel>種別</PropertyLabel>
          <PropertyValue bold>{item.type}</PropertyValue>
        </PropertyDetailRow>
        <PropertyDetailRow>
          <PropertyLabel>築年月</PropertyLabel>
          <PropertyValue bold>{formatYM(item.builtDate)}</PropertyValue>
        </PropertyDetailRow>
        <PropertyDetailRow>
          <PropertyLabel>住所</PropertyLabel>
          <PropertyValue bold>{item.address}</PropertyValue>
        </PropertyDetailRow>
      </PropertyDetails>

      <Divider />

      <ToggleSection>
        {isVisibleToggle && onChangeToggle && (
          <ToggleRow>
            <ToggleLabel>
              売主様の
              <br />
              画面に表示
            </ToggleLabel>
            <Toggle
              defaultChecked={item.isVisible ?? true}
              onChange={onChangeToggle}
            />
          </ToggleRow>
        )}
        <PropertyDetailRow>
          <PropertyLabel>掲載価格</PropertyLabel>
          <PropertyValue bold>{convertPrice(item.salesPrice)}円</PropertyValue>
        </PropertyDetailRow>
      </ToggleSection>
    </Container>
  )
}
